import {
  ActionOutboxEntityType,
  CatalogResults,
  EntityCatalogData,
  Event,
  EventEntities,
  EventEntityCounts,
  EventWithData,
  Listing,
  ListingDetailDataField,
  ListingWithCatalogData,
  Performer,
  Sale,
  SaleWithCatalogData,
  TopLevelCategory,
  Venue,
} from 'src/WebApiController';

import {
  EntitySearchResult,
  SearchDataSummary,
  SearchDataWithEvent,
} from './SearchDataContext.types';

type EntityWithCatalogData = ListingWithCatalogData | SaleWithCatalogData;

/**
 * Computes a unique negative integer key for unmapped venues (venueId = 0).
 *
 * Multiple events can share venueId = 0 (the "unmapped" sentinel). If they were all stored
 * at venues[0], the last one written would overwrite the others, causing unrelated events to
 * display the wrong venue name. Using a deterministic negative hash of the virtualId gives each
 * unmapped-venue event its own unique slot, avoiding the collision.
 *
 * Negative values are safe because real venue IDs are always non-negative.
 */
function getSyntheticVenueId(virtualId: string): number {
  // djb2-style hash — fast and good enough for collision avoidance on a single page
  let hash = 5381;
  for (let i = 0; i < virtualId.length; i++) {
    hash = ((hash << 5) + hash) ^ virtualId.charCodeAt(i);
    hash = hash | 0; // keep it 32-bit
  }
  return hash < 0 ? hash : -(hash || 1);
}

/**
 * Returns the effective venue ID to use as a catalog key and as event.venId.
 * For real venues (venueId > 0), the real ID is used unchanged.
 * For unmapped venues (venueId = 0 or null), a synthetic negative ID derived from the
 * virtualId is used so each unmapped event gets its own unique slot in catalogResults.venues.
 */
function getEffectiveVenueId(
  venueId: number | null | undefined,
  virtualId: string
): number {
  return venueId ? venueId : getSyntheticVenueId(virtualId);
}

/**
 * Builds an Event object from EntityCatalogData.
 * @param catalogData The catalog data containing event information
 * @param virtualId The viagogo virtual ID for the event
 * @returns An Event object or undefined if catalogData is null/undefined
 */
export function buildEventFromEntityCatalogData(
  catalogData: EntityCatalogData | null | undefined,
  virtualId: string
): Event | undefined {
  if (!catalogData?.eventName) {
    return undefined;
  }

  return {
    viagId: null,
    name: catalogData.eventName ?? '',
    shortName: catalogData.eventShortName ?? null,
    rowVer: null,
    posIds: [],
    posId: null,
    virtId: virtualId,
    oldVirtIds: null,
    mpId: null,
    dates: {
      start: catalogData.eventDateTime ?? null,
      end: null,
    },
    datesUtc: {
      start: null,
      end: null,
    },
    onSaleDate: null,
    rawOnSaleDate: null,
    rawPreSaleDate: null,
    evType: null,
    evStat: catalogData.eventStatus ?? null,
    perfId: catalogData.performerId ?? null,
    venId: getEffectiveVenueId(catalogData.venueId, virtualId),
    venCfgId: null,
    genre: catalogData.eventGenre ?? TopLevelCategory.Concert,
    mapRejRsnId: null,
    mapRejDftTxt: null,
    placeholder: null,
  };
}

/**
 * Builds a Performer object from EntityCatalogData.
 * @param catalogData The catalog data containing performer information
 * @returns A Performer object or undefined if performerId is not available
 */
export function buildPerformerFromEntityCatalogData(
  catalogData: EntityCatalogData | null | undefined
): Performer | undefined {
  if (catalogData?.performerId == null) {
    return undefined;
  }

  return {
    viagId: catalogData.performerId,
    name: catalogData.performerName ?? '',
    shortName: null,
    categ: [],
    genreId: null,
    grName: null,
  };
}

/**
 * Builds a Venue object from EntityCatalogData.
 * @param catalogData The catalog data containing venue information
 * @returns A Venue object or undefined if venueId is not available
 */
export function buildVenueFromEntityCatalogData(
  catalogData: EntityCatalogData | null | undefined
): Venue | undefined {
  if (catalogData?.venueId == null) {
    return undefined;
  }

  return {
    viagId: catalogData.venueId,
    name: catalogData.venueName ?? '',
    shortName: null,
    city: catalogData.venueCity ?? null,
    state: catalogData.venueStateProvince ?? null,
    ctryCode: catalogData.venueCountryCode ?? null,
    cap: null,
    excludeSearch: false,
  };
}

interface ExtractedEntities {
  items: EntityWithCatalogData[] | null;
  summary: EntityWithCatalogData | null;
  sample: EntityWithCatalogData | null;
}

/**
 * Extracts items, summary, and a sample entity from event data based on entity type.
 */
function extractEntitiesFromEventData(
  eventData: SearchDataWithEvent[string],
  entityType: ActionOutboxEntityType
): ExtractedEntities {
  if (entityType === ActionOutboxEntityType.Sale) {
    const items = eventData.sales;
    const summary = eventData.salesSummary;
    return {
      items,
      summary,
      sample: items?.[0] ?? summary,
    };
  }
  const items = eventData.listings;
  const summary = eventData.listingsSummary;
  return {
    items,
    summary,
    sample: items?.[0] ?? summary,
  };
}

/**
 * Builds an Event object from embedded catalog data or falls back to existing event data.
 */
function buildEventFromSampleCatalogData(
  sample: EntityWithCatalogData,
  viagogoVirtualId: string,
  eventFallback?: Event
): Event {
  const catalogData = sample.entityCatalogData;
  const builtEvent = buildEventFromEntityCatalogData(
    catalogData,
    viagogoVirtualId
  );

  if (builtEvent) {
    // Augment with sample-specific fields
    return {
      ...builtEvent,
      viagId: sample.viagEvId,
      posIds: sample.posEvId ? [sample.posEvId] : [],
      posId: sample.posEvId,
      mpId: sample.viagEvMapId,
    };
  }
  return eventFallback!;
}

/**
 * Builds and adds a Performer to catalog results from embedded data or fallback.
 */
function addPerformerToCatalogResults(
  sample: EntityWithCatalogData,
  catalogResults: CatalogResults,
  catalogDataFallback?: CatalogResults,
  eventFallback?: Event
): void {
  const catalogData = sample.entityCatalogData;
  const performer = buildPerformerFromEntityCatalogData(catalogData);

  if (performer && catalogData?.performerId != null) {
    catalogResults.performers[catalogData.performerId] = performer;
  } else if (eventFallback?.perfId != null) {
    const performerFallback =
      catalogDataFallback?.performers[eventFallback.perfId];
    if (performerFallback) {
      catalogResults.performers[eventFallback.perfId] = performerFallback;
    }
  }
}

/**
 * Builds and adds a Venue to catalog results from embedded data or fallback.
 */
function addVenueToCatalogResults(
  sample: EntityWithCatalogData,
  catalogResults: CatalogResults,
  virtualId: string,
  catalogDataFallback?: CatalogResults,
  eventFallback?: Event
): void {
  const catalogData = sample.entityCatalogData;
  const venue = buildVenueFromEntityCatalogData(catalogData);

  if (venue && catalogData?.venueId != null) {
    // Use the effective venue ID so unmapped venues (venueId = 0) each get a unique slot,
    // preventing events at different venues from overwriting each other at venues[0].
    const effectiveId = getEffectiveVenueId(catalogData.venueId, virtualId);
    catalogResults.venues[effectiveId] = venue;
  } else if (eventFallback?.venId != null) {
    const venueFallback = catalogDataFallback?.venues[eventFallback.venId];
    if (venueFallback) {
      catalogResults.venues[eventFallback.venId] = venueFallback;
    }
  }
}

/**
 * Builds EventEntityCounts for either sales or listings.
 */
function buildEntityCounts(
  viagogoVirtualId: string,
  sample: EntityWithCatalogData,
  itemCount: number,
  entityType: ActionOutboxEntityType
): EventEntityCounts {
  const catalogData = sample.entityCatalogData;
  return {
    viagVirtId: viagogoVirtualId,
    perfId: catalogData?.performerId ?? null,
    venId: catalogData?.venueId ?? 0,
    rowVer: null,
    salesCnt: entityType === ActionOutboxEntityType.Sale ? itemCount : 0,
    listCnt: entityType === ActionOutboxEntityType.Listing ? itemCount : 0,
    pricedListCnt: 0,
    ungrListCnt: 0,
    listGrpCnt: 0,
    tktGrpsCnt: 0,
    poCnt: 0,
  };
}

/**
 * Builds EventEntities for either sales or listings.
 */
function buildEventEntities(
  eventData: SearchDataWithEvent[string],
  entityType: ActionOutboxEntityType,
  items: EntityWithCatalogData[] | null,
  summary: EntityWithCatalogData | null
): EventEntities {
  // For OpenSearch listings, purchase and tag data is already embedded in the search result,
  // so mark dataIsLoaded flags so columns render without waiting for a secondary fetch
  // that never happens in v2.
  const listings =
    entityType === ActionOutboxEntityType.Listing
      ? ((items as Listing[] | null)?.map((l) => ({
          ...l,
          dataIsLoaded: {
            ...l.dataIsLoaded,
            [ListingDetailDataField.Purchase]: true,
            [ListingDetailDataField.Tags]: true,
          },
        })) ?? null)
      : null;

  return {
    listings,
    listingsSummary:
      entityType === ActionOutboxEntityType.Listing
        ? (summary as Listing)
        : null,
    sales:
      entityType === ActionOutboxEntityType.Sale ? (items as Sale[]) : null,
    salesSummary:
      entityType === ActionOutboxEntityType.Sale ? (summary as Sale) : null,
    ticketGroups: null,
    ticketGroupsSummary: null,
    getDataFail: eventData.failedToRetrieveData ?? null,
  };
}

/**
 * Creates an empty CatalogResults object.
 */
function createEmptyCatalogResults(): CatalogResults {
  return {
    events: {},
    performers: {},
    venues: {},
    viagIdToLatestIdLookup: {},
    posIdToViagIdLookup: {},
    countries: {},
    purchaseCount: 0,
  };
}

/**
 * Generic function to build CatalogResults and EventWithData[] from SearchDataWithEvent.
 * Works for both sales and listings by extracting embedded catalog data.
 */
function buildCatalogAndEventsFromSearchData(
  searchResult: EntitySearchResult,
  searchDataSummary: SearchDataSummary | null | undefined,
  entityType: ActionOutboxEntityType,
  catalogDataFallback?: CatalogResults
): {
  catalogResults: CatalogResults;
  eventsTransformed: EventWithData[];
  totalItemsCount?: number | null;
  maxResultWindow?: number | null;
  pitId?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchAfter?: any[] | null;
} {
  const catalogResults = createEmptyCatalogResults();
  const eventsTransformed: EventWithData[] = [];

  const searchData = searchResult.data;

  // Indicates if the search data summary has been mapped to the event data below
  let isSearchDataSummaryMapped = false;

  // Use sortedGroupIds when present to preserve OpenSearch sort order.
  // Object.entries() reorders integer-like keys numerically per ECMAScript spec,
  // which would override the sort order returned by OpenSearch.
  const orderedIds = searchResult.sortedGroupIds ?? Object.keys(searchData);
  for (const viagogoVirtualId of orderedIds) {
    const eventData = searchData[viagogoVirtualId];
    if (!eventData) continue;
    const { items, summary, sample } = extractEntitiesFromEventData(
      eventData,
      entityType
    );

    if (!sample) {
      continue;
    }

    const eventFallback = catalogDataFallback?.events[viagogoVirtualId]?.event;

    if (!sample.entityCatalogData?.eventName && !eventFallback) {
      continue;
    }

    const event = buildEventFromSampleCatalogData(
      sample,
      viagogoVirtualId,
      eventFallback
    );

    addPerformerToCatalogResults(
      sample,
      catalogResults,
      catalogDataFallback,
      eventFallback
    );
    addVenueToCatalogResults(
      sample,
      catalogResults,
      viagogoVirtualId,
      catalogDataFallback,
      eventFallback
    );

    const itemCount = searchResult.totalItemsCount ?? items?.length ?? 0;
    const counts = buildEntityCounts(
      viagogoVirtualId,
      sample,
      itemCount,
      entityType
    );

    // If searchDataSummary is available, only use it. And only need to use it once
    // Any other cases, try to use the summary from the search data
    const summaryReconciled =
      searchDataSummary != null
        ? isSearchDataSummaryMapped
          ? null
          : entityType === ActionOutboxEntityType.Sale
            ? searchDataSummary.salesSummary
            : searchDataSummary.listingsSummary
        : summary;

    isSearchDataSummaryMapped = true;

    const entities = buildEventEntities(
      eventData,
      entityType,
      items,
      summaryReconciled
    );

    const eventWithData: EventWithData = {
      event,
      counts,
      entities,
    };

    catalogResults.events[viagogoVirtualId] = eventWithData;
    catalogResults.viagIdToLatestIdLookup[viagogoVirtualId] = viagogoVirtualId;
    eventsTransformed.push(eventWithData);
  }

  return {
    catalogResults,
    eventsTransformed,
    totalItemsCount: searchResult.totalItemsCount,
    maxResultWindow: searchResult.maxResultWindow,
    pitId: searchResult.pitId,
    searchAfter: searchResult.searchAfter,
  };
}

/**
 * Builds CatalogResults and EventWithData[] from SearchDataWithEvent for sales.
 * This extracts embedded catalog data from SaleWithCatalogData objects.
 */
export function buildCatalogAndEventsFromSalesSearchData(
  searchData: EntitySearchResult,
  searchDataSummary: SearchDataSummary | null | undefined,
  catalogDataFallback?: CatalogResults
): {
  catalogResults: CatalogResults;
  eventsTransformed: EventWithData[];
} {
  return buildCatalogAndEventsFromSearchData(
    searchData,
    searchDataSummary,
    ActionOutboxEntityType.Sale,
    catalogDataFallback
  );
}

/**
 * Builds CatalogResults and EventWithData[] from SearchDataWithEvent for listings.
 * This extracts embedded catalog data from ListingWithCatalogData objects.
 */
export function buildCatalogAndEventsFromListingsSearchData(
  searchData: EntitySearchResult,
  searchDataSummary: SearchDataSummary | null | undefined,
  catalogDataFallback?: CatalogResults
): {
  catalogResults: CatalogResults;
  eventsTransformed: EventWithData[];
} {
  return buildCatalogAndEventsFromSearchData(
    searchData,
    searchDataSummary,
    ActionOutboxEntityType.Listing,
    catalogDataFallback
  );
}

/**
 * Resolves the event IDs to pass to the search API.
 *
 * When the user has selected specific events (eventOrMappingIds is non-empty), those are used
 * directly. Otherwise we fall back to the catalog-resolved event IDs from CatalogDataContext,
 * which already match the same filter query sent to V1.
 *
 * Zero GUIDs ("00000000-0000-0000-0000-000000000000") are stripped from the fallback path —
 * C# serializes Guid.Empty as that string for unmapped events, and passing it as a POSEventId
 * filter would return zero results.
 *
 * Additionally, for backward compatibility, `old:<posId>` variants are appended for any event
 * that has POS IDs in the catalog, so the search can match documents indexed under legacy IDs.
 */
export function buildResolvedEventIds(
  eventOrMappingIds: string[] | null | undefined,
  catalogAllEventIds: { viagVirtId: string }[] | null | undefined,
  catalogEvents: CatalogResults['events'] | null | undefined
): string[] {
  const baseIds: string[] = eventOrMappingIds?.length
    ? eventOrMappingIds
    : (catalogAllEventIds
        ?.map((e) => e.viagVirtId)
        .filter(
          (id): id is string =>
            id != null && id !== '00000000-0000-0000-0000-000000000000'
        ) ?? []);

  if (catalogEvents == null || baseIds.length === 0) {
    return baseIds;
  }

  const posIds = baseIds.flatMap((id) =>
    (catalogEvents[id]?.event?.posIds ?? []).map((posId) => `old:${posId}`)
  );

  return posIds.length > 0 ? [...baseIds, ...posIds] : baseIds;
}

/**
 * Reorders a flat array of entities to match the globally-sorted order returned by OpenSearch.
 *
 * When the search response groups items by event, a single event group can contain items of
 * mixed sort rank (e.g. one event with both Transfer and Paper tickets). Flattening event-by-event
 * would put those mixed items out of the correct global order. sortedItemIds is the flat list of
 * item IDs in the exact order OpenSearch returned them, and we use it here to restore that order.
 *
 * Items whose ID is not found in sortedItemIds (should not happen in practice) are placed at the
 * end of the result to avoid silently dropping data.
 *
 * Note: JavaScript's number type (IEEE-754 double) safely represents integers up to 2^53 - 1
 * (~9 quadrillion). StubHub entity IDs are currently in the billions range, well within this
 * limit, so no precision loss occurs.
 */
export function reorderByIds<T extends { id: number }>(
  items: T[],
  sortedItemIds: number[] | null | undefined
): T[] {
  if (!sortedItemIds?.length) return items;
  const idToIndex = new Map(sortedItemIds.map((id, idx) => [id, idx]));
  return [...items].sort(
    (a, b) =>
      (idToIndex.get(a.id) ?? Infinity) - (idToIndex.get(b.id) ?? Infinity)
  );
}
